// frontend/src/components/fleet/FleetMapPanel.jsx
//
// Mapa GPS de la flota, pensado para ir AL LADO de la vista lista.
//
//  - Marcadores por vehículo, coloreados por estado operativo.
//  - Geocercas dibujadas (polígono / círculo / rectángulo).
//  - Selección sincronizada con la lista en ambos sentidos.
//  - Estado vacío explícito cuando no hay GPS (nunca un mapa mudo).
//
// El dibujo de geocercas nuevas se hace aquí mismo (click a click) para no
// depender de la librería de drawing de Google.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  MyLocation as CenterIcon,
  LocationOff as NoLocationIcon,
} from '@mui/icons-material';
import { GoogleMap, Marker, InfoWindow, Polygon, Circle, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 }; // Centro geográfico de México
const DEFAULT_ZOOM = 5;

const STATUS_COLORS = {
  alarma: '#d32f2f',
  atencion: '#ed6c02',
  ok: '#2e7d32',
  parcial: '#9e9e9e',
  sin_comunicacion: '#616161',
  sin_sensores: '#bdbdbd',
};

export const statusColor = (status) => STATUS_COLORS[status] || '#616161';

const FleetMapPanel = ({
  markers = [],
  geofences = [],
  selectedVehicleId = null,
  onSelectVehicle,
  loading = false,
  height = 520,
  drawingMode = null,          // 'polygon' | 'circle' | null
  drawnPoints = [],
  onMapClick,
  emptyMessage,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [map, setMap] = useState(null);
  const [infoVehicleId, setInfoVehicleId] = useState(null);
  const didFitRef = useRef(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    // Mismo id que el LoadScript del modal de telemetría: así el script se
    // carga una sola vez aunque ambos convivan.
    id: 'script-loader',
  });

  const positioned = useMemo(
    () => markers.filter((m) => m.latitude !== null && m.latitude !== undefined
      && m.longitude !== null && m.longitude !== undefined),
    [markers],
  );

  const onLoad = useCallback((instance) => setMap(instance), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const fitToMarkers = useCallback(() => {
    if (!map || !window.google?.maps || positioned.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    positioned.forEach((m) => bounds.extend({ lat: Number(m.latitude), lng: Number(m.longitude) }));
    geofences.forEach((f) => {
      (f.coordinates || []).forEach((p) => bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) }));
      if (f.center_lat !== null && f.center_lat !== undefined) {
        bounds.extend({ lat: Number(f.center_lat), lng: Number(f.center_lng) });
      }
    });
    map.fitBounds(bounds, 48);
  }, [map, positioned, geofences]);

  // Encuadre inicial una sola vez: reencuadrar en cada refresco marea al usuario.
  useEffect(() => {
    if (!didFitRef.current && map && positioned.length > 0) {
      didFitRef.current = true;
      fitToMarkers();
    }
  }, [map, positioned, fitToMarkers]);

  // Centrar en el vehículo seleccionado desde la lista.
  useEffect(() => {
    if (!map || !selectedVehicleId) return;
    const target = positioned.find((m) => m.vehicle_id === selectedVehicleId);
    if (!target) return;
    map.panTo({ lat: Number(target.latitude), lng: Number(target.longitude) });
    setInfoVehicleId(selectedVehicleId);
  }, [map, selectedVehicleId, positioned]);

  const handleMapClick = useCallback((event) => {
    if (!drawingMode || !onMapClick) return;
    onMapClick({ lat: event.latLng.lat(), lng: event.latLng.lng() });
  }, [drawingMode, onMapClick]);

  if (loadError) {
    return (
      <Paper sx={{ p: 3, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ width: '100%' }}>
          No se pudo cargar Google Maps. Revisa la clave de API configurada.
        </Alert>
      </Paper>
    );
  }

  if (!isLoaded) {
    return (
      <Paper sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  const infoMarker = positioned.find((m) => m.vehicle_id === infoVehicleId);
  // `emptyMessage === null` desactiva el aviso por completo (p. ej. la pantalla
  // de geocercas, donde no tener vehículos con GPS es lo normal).
  const showEmptyState = emptyMessage !== null
    && positioned.length === 0
    && !loading
    && geofences.length === 0
    && drawnPoints.length === 0
    && !drawingMode;

  return (
    <Paper sx={{ position: 'relative', height, overflow: 'hidden' }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
        options={{
          mapTypeId: 'roadmap',
          streetViewControl: false,
          fullscreenControl: true,
          mapTypeControl: true,
          zoomControl: true,
          draggableCursor: drawingMode ? 'crosshair' : undefined,
        }}
      >
        {/* Geocercas de la empresa */}
        {geofences.map((fence) => {
          const color = fence.color || theme.palette.primary.main;
          const shapeOptions = {
            fillColor: color,
            fillOpacity: 0.12,
            strokeColor: color,
            strokeOpacity: 0.85,
            strokeWeight: 2,
            clickable: false,
          };
          if (fence.geometry_type === 'circle'
            && fence.center_lat !== null && fence.center_lat !== undefined) {
            return (
              <Circle
                key={fence.id}
                center={{ lat: Number(fence.center_lat), lng: Number(fence.center_lng) }}
                radius={Number(fence.radius_meters) || 0}
                options={shapeOptions}
              />
            );
          }
          const path = (fence.coordinates || []).map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
          if (path.length < 3) return null;
          return <Polygon key={fence.id} paths={path} options={shapeOptions} />;
        })}

        {/* Geocerca en construcción */}
        {drawnPoints.length >= 3 && (
          <Polygon
            paths={drawnPoints}
            options={{
              fillColor: theme.palette.secondary.main,
              fillOpacity: 0.15,
              strokeColor: theme.palette.secondary.main,
              strokeWeight: 2,
              clickable: false,
            }}
          />
        )}
        {drawnPoints.map((point, index) => (
          <Marker
            key={`draft-${index}`}
            position={point}
            label={{ text: String(index + 1), color: '#fff', fontSize: '10px' }}
          />
        ))}

        {/* Vehículos */}
        {positioned.map((marker) => {
          const isSelected = marker.vehicle_id === selectedVehicleId;
          const color = statusColor(marker.status);
          return (
            <Marker
              key={marker.vehicle_id}
              position={{ lat: Number(marker.latitude), lng: Number(marker.longitude) }}
              title={marker.vehicle_name}
              zIndex={isSelected ? 999 : 1}
              onClick={() => {
                setInfoVehicleId(marker.vehicle_id);
                if (onSelectVehicle) onSelectVehicle(marker.vehicle_id);
              }}
              icon={window.google?.maps ? {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: isSelected ? 11 : 7,
                fillColor: color,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: isSelected ? 3 : 2,
              } : undefined}
            />
          );
        })}

        {infoMarker && (
          <InfoWindow
            position={{ lat: Number(infoMarker.latitude), lng: Number(infoMarker.longitude) }}
            onCloseClick={() => setInfoVehicleId(null)}
          >
            <Box sx={{ minWidth: 150 }}>
              <Typography variant="subtitle2" fontWeight={700}>{infoMarker.vehicle_name}</Typography>
              {infoMarker.speed_kmh !== null && infoMarker.speed_kmh !== undefined && (
                <Typography variant="caption" display="block">
                  {t('hardcoded.components_fleet.velocidad')}: {infoMarker.speed_kmh} km/h
                </Typography>
              )}
              {!!infoMarker.open_tickets && (
                <Chip size="small" color="error" label={`${infoMarker.open_tickets} ticket(s)`} sx={{ mt: 0.5 }} />
              )}
            </Box>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Estado vacío: SOLO cuando el mapa está realmente vacío. Si hay
          geocercas dibujadas o se está dibujando una, el mapa ya comunica algo
          y un velo encima estorbaría. */}
      {showEmptyState && (
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 1,
          bgcolor: 'rgba(255,255,255,0.88)', pointerEvents: 'none', textAlign: 'center', px: 3,
        }}>
          <NoLocationIcon sx={{ fontSize: 42, color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary">
            {emptyMessage || t('hardcoded.components_fleet.sin_ubicacion_gps')}
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 10, right: 10 }}>
        {loading && <CircularProgress size={22} />}
        <Tooltip title="Encuadrar la flota">
          <span>
            <IconButton
              size="small"
              onClick={fitToMarkers}
              disabled={positioned.length === 0}
              sx={{ bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
            >
              <CenterIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

FleetMapPanel.propTypes = {
  markers: PropTypes.array,
  geofences: PropTypes.array,
  selectedVehicleId: PropTypes.string,
  onSelectVehicle: PropTypes.func,
  loading: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  drawingMode: PropTypes.string,
  drawnPoints: PropTypes.array,
  onMapClick: PropTypes.func,
  emptyMessage: PropTypes.string,
};

export default FleetMapPanel;
